import { useState, useCallback, useRef } from "react";
import { ethers } from "ethers";
import { getEthereumProvider } from "../contexts/WalletContext";
import { RPC_URLS } from "../constants";

// Track which fallback RPC index worked last so we start from it next time
let _lastGoodRpcIdx = 0;

function isRetryableError(error) {
    const code = error?.code;
    if (code === "BAD_DATA" || code === "SERVER_ERROR" ||
        code === "TIMEOUT" || code === "NETWORK_ERROR") return true;
    const msg = error?.message?.toLowerCase() || "";
    if (msg.includes("timeout") || msg.includes("bad response") || msg.includes("missing response") ||
        msg.includes("could not decode") || msg.includes("server error") || msg.includes("rate limit") ||
        msg.includes("429") || msg.includes("502") || msg.includes("503")) return true;
    return false;
}

export const useRPCProvider = () => {
    const [rpcStatus, setRpcStatus] = useState("unknown");
    const [activeRpc, setActiveRpc] = useState("Wallet RPC");
    const retryCountRef = useRef(0);

    const getWalletProvider = useCallback(() => {
        const ethProvider = getEthereumProvider();
        if (!ethProvider) return null;
        return new ethers.BrowserProvider(ethProvider);
    }, []);

    const getWalletSigner = useCallback(() => {
        const ethProvider = getEthereumProvider();
        if (!ethProvider) return null;
        return new ethers.BrowserProvider(ethProvider).getSigner();
    }, []);

    const executeWithFailover = useCallback(
        async (fn, options = {}) => {
            const { timeout = 10000 } = options;

            const urls = RPC_URLS.BSC_MAINNET;

            // Step 1: Try wallet RPC first
            const walletProvider = getWalletProvider();
            if (walletProvider) {
                try {
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error("Request timeout")), timeout)
                    );
                    const result = await Promise.race([fn(walletProvider), timeoutPromise]);
                    setRpcStatus("healthy");
                    setActiveRpc(urls[_lastGoodRpcIdx]);
                    retryCountRef.current = 0;
                    return result;
                } catch (error) {
                    if (!isRetryableError(error)) {
                        // Non-retryable (e.g. contract revert) — no point trying other RPCs
                        setRpcStatus("error");
                        retryCountRef.current++;
                        throw error;
                    }
                    // Retryable — fall through to RPC list
                }
            }

            // Step 2: Wallet failed or not available — try RPC list one by one
            if (!urls?.length) {
                throw new Error("Wallet RPC failed and no fallback RPC URLs configured.");
            }

            let lastError = null;
            for (let attempt = 0; attempt < urls.length; attempt++) {
                const idx = (_lastGoodRpcIdx + attempt) % urls.length;
                const provider = new ethers.JsonRpcProvider(urls[idx]);
                try {
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error("Request timeout")), timeout)
                    );
                    const result = await Promise.race([fn(provider), timeoutPromise]);
                    _lastGoodRpcIdx = idx;
                    setRpcStatus("healthy");
                    retryCountRef.current = 0;
                    return result;
                } catch (error) {
                    lastError = error;
                    if (!isRetryableError(error)) {
                        break;
                    }
                }
            }

            setRpcStatus("error");
            retryCountRef.current++;
            throw lastError;
        },
        [getWalletProvider]
    );

    const getRPCInfo = useCallback(() => {
        return {
            status: rpcStatus,
            retryCount: retryCountRef.current,
        };
    }, [rpcStatus]);

    return {
        getProvider: getWalletProvider,
        executeWithFailover,
        getRPCInfo,
        rpcStatus,
        activeRpc,
        getWalletSigner,
    };
};
